/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/*
 * While onboarding another app in cjm.js, please identify
 * if there are sharable configs among apps and if yes, then
 * create a baseConfig object and move common configs there
*/

import {HISTORY, RELEASE_TYPE, Solution} from '@exc/core/src/models/Solution';
const sideNav = {workHubId: 'CJM'};

const baseConfig = {
  access: {
    code: ['acp'],
    codeExt: {
      code: 'bso',
      fi: 'cjm_foundation'
    },
    codeFlagLogicOp: 'AND',
    codeLogicOp: 'AND',
    sandboxFlag: 'cjm-enabled-alpha'
  },
  releaseType: RELEASE_TYPE.BETA,
  restrictBySandbox: true
};

export const cjmHome: Solution = {
  ...baseConfig,
  appId: 'cjm-home',
  distributionList: ['Grp-TrojanRabbit'],
  path: '/cjm/home',
  redirectFrom: ['/cjm'],
  spaAppId: 'exc-cjm-home',
  sandbox: {
    history: HISTORY.HISTORY,
    sources: {
      dev: 'https://localhost.corp.adobe.com:12399'
    }
  },
  sideNav,
  urlContext: {key: 'sandbox'}
};

export const cjmAdmin: Solution = {
  ...baseConfig,
  appId: 'cjm-admin',
  distributionList: ['GRP-VOYAGER'],
  path: '/cjm/admin',
  sandbox: {
    history: HISTORY.HISTORY,
    ims: {
      client_id: 'voyager',
      scope: 'AdobeID,openid,additional_info.projectedProductContext,read_organizations'
    },
    pathPrefix: '/admin',
    sources: {
      dev: 'https://localhost.corp.adobe.com/admin',
      qa: 'https://journeys-dev.adobe.com/admin',
      stage: 'https://journeys-stage.adobe.com/admin',
      prod: 'https://journeys.adobe.com/admin'
    }
  },
  sideNav,
  urlContext: {key: 'sandbox'}
};

export const cjmPlatform: Solution = {
  ...baseConfig,
  appId: 'cjm-platform',
  distributionList: [`Grp-platform-ui@adobe.com`],
  path: '/cjm/platform',
  serviceCode: 'acp',
  sandbox: {
    history: HISTORY.HISTORY,
    sources: {
      dev: 'https://localhost.dev.cloud.adobe.io',
      qa: 'https://platform-int.adobe.com',
      stage: 'https://platform-stage.adobe.com',
      prod: 'https://platform.adobe.com'
    }
  },
  sideNav,
  urlContext: {key: 'sandbox'}
};

export const cjmMessage: Solution = {
  ...baseConfig,
  appId: 'cjm-message',
  distributionList: ['Grp-Content-Authoring-India'],
  path: '/cjm/messages',
  sandbox: {
    history: HISTORY.HISTORY,
    sources: {
      dev: 'https://localhost.corp.adobe.com:8443'
    }
  },
  sideNav,
  spaAppId: 'cjm-message-ui',
  urlContext: {key: 'sandbox'}
};

export const cjmLiveReport: Solution = {
  ...baseConfig,
  appId: 'cjm-live-report',
  distributionList: ['Grp-cjm-live-reporting-ui-team'],
  path: '/cjm/livereports',
  sandbox: {
    history: HISTORY.HISTORY,
    sources: {
      dev: 'https://localhost.corp.adobe.com:3000'
    },
    ims: {
      client_id: 'cjm-live-report',
      scope: 'AdobeID, openid, read_organizations, ab.manage, additional_info, additional_info.projectedProductContext, additional_info.roles, read_pc.acp, read_pc, read_pc.dma_tartan'
    }
  },
  sideNav,
  spaAppId: 'cjm-live-reporting-ui',
  urlContext: {key: 'sandbox'}
};

// The solution config for the Journeys Orchestration app
export const cjmJourneys: Solution = {
  ...baseConfig,
  appId: 'cjm-journeys',
  distributionList: ['GRP-VOYAGER'],
  path: '/cjm/journeys',
  sandbox: {
    history: HISTORY.HISTORY,
    ims: {
      client_id: 'voyager',
      scope: 'AdobeID,openid,additional_info.projectedProductContext,read_organizations'
    },
    sources: {
      dev: 'https://localhost.corp.adobe.com/',
      qa: 'https://journeys-dev.adobe.com/',
      stage: 'https://journeys-stage.adobe.com/',
      prod: 'https://journeys.adobe.com/'
    }
  },
  sideNav,
  urlContext: {key: 'sandbox'}
};

export const cjmEmailDesigner: Solution = {
  ...baseConfig,
  appId: 'cjm-email-designer',
  distributionList: ['ORG-FINA-ALL@adobe.com'],
  path: '/cjm/content/email-designer',
  redirectFrom: ['/cjm/email-designer'],
  spaAppId: 'pixel-acrites-ui',
  sandbox: {
    hideInitialSpinner: true,
    history: HISTORY.HASH,
    sources: {
      dev: 'https://localhost.rd.campaign.adobe.com:9000/'
    }
  },
  sideNav: {...sideNav, closeOnLoad: true},
  urlContext: {key: 'sandbox'}
};

export const cjmConfigUi: Solution = {
  ...baseConfig,
  appId: 'cjm-configui',
  distributionList: ['Grp-cjm-config-engg'],
  path: '/cjm/configui',
  spaAppId: 'exc-cjm-config-ui',
  sandbox: {
    history: HISTORY.HISTORY,
    sources: {
      dev: 'https://localhost.corp.adobe.com:8013',
      prod: '' //TODO: Remove this line before the production release.
    }
  },
  sideNav,
  urlContext: {key: 'sandbox'}
};
